"use client";

/**
 * MarqueeText — shows text on one line; when it's too long to fit it scrolls the
 * full text into view, then back. No scrolling when the text already fits.
 *
 *  • On appear (e.g. a dialog/popup opening): scrolls through ONCE so the full
 *    title is seen even without hovering.
 *  • Desktop (hover-capable): scrolls while the pointer is over it.
 *  • Touch devices (no hover): also auto-scrolls once every 6 seconds.
 *
 * (Gordon: "track titles that are cut off need to scroll on mouse-over and when
 *  the popup loads.")
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export function MarqueeText({ text, className }: { text: string; className?: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [shift, setShift] = useState(false);

  // Measure how far the text overflows its container (0 when it fits).
  useEffect(() => {
    const measure = () => {
      const w = wrapRef.current, t = innerRef.current;
      if (!w || !t) return;
      setOverflow(Math.max(0, t.scrollWidth - w.clientWidth));
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => ro?.disconnect();
  }, [text]);

  // Scroll through ONCE shortly after the text appears (so when a popup opens
  // the full title is revealed without needing to hover). Runs on every device.
  useEffect(() => {
    if (overflow <= 0) return;
    let back: ReturnType<typeof setTimeout>;
    const out = setTimeout(() => {
      setShift(true);
      back = setTimeout(() => setShift(false), Math.max(2200, (overflow / 40) * 1000 + 600));
    }, 500);
    return () => { clearTimeout(out); clearTimeout(back); };
  }, [overflow]);

  // Periodic auto-scroll on EVERY device — touch every 6s, desktop every 9s
  // (Gordon: "too long titles scroll by so you can read it every like 9
  // seconds"). Desktop additionally scrolls on hover (handlers below); the
  // interval just guarantees the full title is readable without interacting.
  useEffect(() => {
    if (overflow <= 0) return;
    const canHover = typeof window !== "undefined" && window.matchMedia?.("(hover: hover)")?.matches;
    const period = canHover ? 9000 : 6000;
    const dwell = Math.max(3000, (overflow / 40) * 1000 + 800); // time fully scrolled out
    let backTimer: ReturnType<typeof setTimeout>;
    const id = setInterval(() => {
      setShift(true);
      backTimer = setTimeout(() => setShift(false), dwell);
    }, period);
    return () => { clearInterval(id); clearTimeout(backTimer); };
  }, [overflow, text]);

  const duration = Math.max(2, overflow / 40); // ~40px/sec scroll speed

  return (
    <div
      ref={wrapRef}
      className={cn("overflow-hidden", className)}
      onMouseEnter={() => overflow > 0 && setShift(true)}
      onMouseLeave={() => setShift(false)}
    >
      <span
        ref={innerRef}
        className="inline-block whitespace-nowrap align-bottom will-change-transform"
        style={{
          transform: shift && overflow > 0 ? `translateX(-${overflow}px)` : "translateX(0)",
          transition: `transform ${shift ? duration : 0.4}s linear`,
        }}
      >
        {text}
      </span>
    </div>
  );
}
